import { useRef } from "react";

/**
 * Custom hook to manage image caching and loading.
 * - Preloads images with optional priority
 * - Returns props for <img> for lazy/eager loading
 * - Handles cache cleanup for large galleries
 */
export function useImageLoader(maxCacheSize = 100) {
  const cache = useRef({});

  /**
   * Preload an image and store in cache
   * @param {string} src - Image URL
   */
  const preload = (src) => {
    if (!src || cache.current[src]) return;

    const img = new Image();
    img.src = src;
    cache.current[src] = img;

    // Optional: automatic cache cleanup if too large
    if (Object.keys(cache.current).length > maxCacheSize) {
      const keys = Object.keys(cache.current).slice(0, 20); // remove oldest 20
      keys.forEach((key) => delete cache.current[key]);
    }
  };

  /**
   * Get image props for <img>
   * @param {string} src - Image URL
   * @param {boolean} eager - load immediately if true
   * @returns {object} props to spread on <img>
   */
  const getImageProps = (src, eager = false) => {
    preload(src);
    return {
      src,
      loading: eager ? "eager" : "lazy",
      decoding: "async",
      fetchpriority: eager ? "high" : "low",
    };
  };

  /**
   * Preload a batch of images
   * @param {string[]} srcArray - Array of image URLs
   * @param {boolean} eager - preload immediately
   */
  const preloadBatch = (srcArray = [], eager = false) => {
    srcArray.forEach((src) => {
      if (eager) {
        preload(src);
      } else if (!cache.current[src]) {
        // lazy preload by creating an Image object but not blocking
        const img = new Image();
        img.src = src;
        cache.current[src] = img;
      }
    });
  };

  return {
    cache,
    preload,
    getImageProps,
    preloadBatch,
  };
}
